
import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator";
import { CustomFieldDto, CustomFieldExtendedDataEmitValue, CustomFieldTypes, CustomFieldValues } from "@/store/custom-fields/types";
import { truncateString } from "@/filters";
import IconUpload from "@/components/IconUpload.vue";
import CustomFieldDatePicker from "@/components/custom-fields/CustomFieldDatePicker.vue";
import CustomDefinedFieldExtendedData from "@/components/custom-fields/CustomDefinedFieldExtendedData.vue";
import { customFieldMixins } from "@/mixins/customFieldMixins";
import { Constants } from "@/types/Constants";
import { CustomFieldConstants } from "@/mixins/customFieldMixins";
import vSelect3 from "vselect3";
import { Getter } from "vuex-class";

const { convertCustomFieldDateValue } = customFieldMixins.methods;

@Component({
	filters: { truncateString },
	components: {
		"icon-upload": IconUpload,
		"custom-field-date-picker": CustomFieldDatePicker,
		"v-select-3": vSelect3,
		"custom-defined-field-extended-data": CustomDefinedFieldExtendedData,
	}
})

export default class CustomDefinedField extends Vue {
	@Prop({ type: Object, required: true }) public value: CustomFieldValues;
	@Prop({ type: Object, required: true }) public setting: CustomFieldDto;
	@Prop({ type: Number, default: 250 }) public maxCharacters: number;
	@Prop({ type: Boolean, default: false}) public isEditable: boolean;
	@Prop({ type: Boolean, default: false }) public isStatusDisplay: boolean;
	@Prop({ type: Boolean, default: false }) public belongsToGroupSync: boolean;

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	private type: string;
	private CFListDelimiter: string = CustomFieldConstants.StringDelimiter;
	private CFTextLength: number = CustomFieldConstants.CF_TEXT_LENGTH;
	private CFTextAreaLength: number = CustomFieldConstants.CF_TEXTAREA_LENGTH;
	private CFHyperlinkLength: number = CustomFieldConstants.CF_HYPERLINK_LENGTH;
	private CFNumberMax: number = Constants.MAXIMUM_INTEGER_VALUE;
	private CFNumberMin: number = Constants.MINIMUM_INTEGER_VALUE;

	private extendedDataOpen: boolean = false;
	private populatedByExtendedDataText: string = null;

	@Watch("type")
	private beforeMount(): void {
		this.type = CustomFieldTypes[this.setting?.customFieldTypeId];
	}

	@Watch("value")
	private watchValue(newVal, oldVal): void {
		if(!newVal.value || newVal.value !== oldVal.value) {
			this.setting.populatedByExtendedData = false
			this.populatedByExtendedDataText = null;
		}
	}

	public get isActionPlanResultFeatureEnabled(): boolean {
		return !!this.getFeature(["CustomFields", "NotificationTemplates", "ExtendedData", "ActionPlanResult"]);
	}

	public get isExtendedDataOpen(): boolean {
		return this.isActionPlanResultFeatureEnabled && !!this.setting.options.dataFromActionPlans;
	}

	private get checkboxValue(): boolean {
		return this.value.value == "true" ? true : false;
	}

	private get customFieldNumberState(): boolean {
		if(this.value.value) {
			return this.value.value <= this.CFNumberMax && this.value.value >= this.CFNumberMin;
		}

		return true;
    }

	private getConvertedCustomFieldDateValue(date: string): string {
		return convertCustomFieldDateValue(date);
	}

	private get typeToLowerCase(): string {
		return this.type?.toLowerCase();
	}

	private isType(customFieldType: string): boolean {
		if (!(customFieldType in CustomFieldTypes)) {
			return false;
		}

		return this.typeToLowerCase === customFieldType?.toLowerCase();
	}

	private updateValue(emittedValue: string): void {
		if (this.value?.value != emittedValue) {
			this.value.value = emittedValue;
			this.onValueChange();
		}
	}

	private onSelected(updatedValue): void {
		if (!updatedValue) {
			this.value.value = "";
		} else {
			this.value.value = updatedValue;
		}

		this.onValueChange();
	}

	private onValueChange(): void {
		if (this.setting.customFieldTypeId == CustomFieldTypes.List && typeof(this.value.value) == 'object') {
			this.value.value = this.value.value.join(this.CFListDelimiter);
		}
		this.$emit('CFValueChanged', this.value);
	}

	private get isCustomFieldSyncControlled(): boolean {
		return this.belongsToGroupSync &&
			this.setting.externalIds != null &&
			this.setting.externalIds.length > 0;
	}

	/**
	* Computed property to get the value for v-select-3.
	* It splits the value by "CFStringDelimiter" if it exists, otherwise returns an empty array.
	*/
	private get valueForSelect(): string[] {
		const currentValue = this.value?.value;
		if (typeof currentValue === 'string' && currentValue.trim()) {
			return this.isType('List')
				? currentValue.split(this.CFListDelimiter)
				: this.isType('TextArea')
					? currentValue.split("\r\n")
					: [currentValue.trim()];
		}

		return [];
	}

	private updateCustomFieldValue(emitValue: CustomFieldExtendedDataEmitValue): void {
		if (!emitValue || !emitValue.value) {
			this.populatedByExtendedDataText = null;
			return;
		}

		if (this.isType('List')) {
			if (this.setting.options.allowMultiple && typeof this.value.value === 'string' && this.value.value !== '') {
				const updateValue = this.value.value.split(this.CFListDelimiter);

				// Theres a bug that when navigating away and back to the form,
				// sometimes the value gets prefixed with " - " multiple times.
				// Remove any starting " - " to clean it up.
				let val = emitValue.value;
				if (val.startsWith(" - ")) {
					val = val.replace(` - `, "");
				}
				if(!updateValue.includes(val)) {
					updateValue.push(val);
				}
				this.value.value = updateValue.join(this.CFListDelimiter);
			} else {
				this.value.value = emitValue.value;
			}
			this.$emit('CFValueChanged', this.value);
			this.setting.populatedByExtendedData = true;
			this.populatedByExtendedDataText = emitValue.updateMessage;
		} else if (this.isType('Text')) {
			this.value.value = emitValue.value;
			this.$emit('CFValueChanged', this.value);
			this.setting.populatedByExtendedData = true;
			this.populatedByExtendedDataText = emitValue.updateMessage;
		} else if (this.isType('TextArea')) {
			let updateValue = "";
			if (typeof this.value.value === 'string' && this.value.value && this.setting.options.allowMultiple) {
				updateValue = this.value.value.trim() ? this.value.value + "\r\n" : "";
			}
			this.value.value = updateValue + emitValue.value;
			this.$emit('CFValueChanged', this.value);
			this.setting.populatedByExtendedData = true;
			this.populatedByExtendedDataText = emitValue.updateMessage;
		}
	}

	@Emit("show-action-plan-alert")
	private onActionPlanResult(value: boolean): boolean {
		return value;
	}

	private get showActionPlanMessage() {
		return this.setting.populatedByExtendedData && this.populatedByExtendedDataText
	}
}
